import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, SkeletonText, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { TaxRegimeCode } from '@his/shared';

interface RegimeCard {
  title: string;
  note: string;
  taxNoHint?: string;
  localName: boolean;
  localNameHint?: string;
}

export const REGIME_CARDS: Record<TaxRegimeCode, RegimeCard> = {
  sa_zatca: {
    title: 'Saudi tax invoice (ZATCA phase 1)',
    note: 'With a VAT number entered, printed invoices and credit notes carry the tax invoice title, the seller details and the QR code, in English and Arabic. Reporting invoices to ZATCA (phase 2, with certificates and the cryptographic stamp) is not part of this system.',
    taxNoHint: '15 digits, starting and ending with 3. Leave empty if this branch is not VAT-registered.',
    localName: true,
    localNameHint: 'Encoded in the QR code when set.',
  },
  ae_vat: {
    title: 'UAE tax invoice',
    note: 'With a TRN entered, printed invoices carry the Tax Invoice title and the TRN. No QR code is printed; that belongs to Saudi invoices only.',
    taxNoHint: '15 digits. Leave empty if this branch is not VAT-registered.',
    localName: true,
    localNameHint: 'Printed beside the English name on tax invoices.',
  },
  uk_vat: {
    title: 'UK VAT invoice',
    note: 'Medical care is usually exempt from VAT. With a VAT registration number entered, invoices that charge VAT are titled VAT invoice; the rest print as ordinary invoices.',
    taxNoHint: 'GB followed by nine digits.',
    localName: false,
  },
  pk_sales_tax: {
    title: 'Pakistan sales tax',
    note: 'With an NTN entered, it is printed on invoices, and invoices that charge sales tax are titled Sales Tax Invoice.',
    localName: false,
  },
  co_iva: {
    title: 'Colombia (preview)',
    note: 'Until electronic invoicing is connected, printed documents are titled Prefactura and state that they are not an electronic sales invoice.',
    taxNoHint: 'The NIT with its check digit, for example 900123456-8.',
    localName: false,
  },
  none: {
    title: 'Tax',
    note: 'Tax: not applicable in this country. Invoices print without tax details.',
    localName: false,
  },
};

interface BranchTaxProfileView {
  branchId: string;
  country: string | null;
  currency: string | null;
  regime: TaxRegimeCode;
  registrationLabel: string | null;
  companyRegistrationLabel: string | null;
  documentSecondary: string | null;
  secondaryOnTaxDocuments: 'mandatory' | 'default_on' | 'off';
  profile: {
    registrationNo: string | null;
    legalName: string | null;
    legalNameLocal: string | null;
    companyRegNo: string | null;
  } | null;
}

interface TaxForm {
  registrationNo: string;
  legalName: string;
  legalNameLocal: string;
  companyRegNo: string;
}

const EMPTY: TaxForm = { registrationNo: '', legalName: '', legalNameLocal: '', companyRegNo: '' };

function formOf(view: BranchTaxProfileView | undefined): TaxForm {
  const profile = view?.profile;
  if (!profile) return EMPTY;
  return {
    registrationNo: profile.registrationNo ?? '',
    legalName: profile.legalName ?? '',
    legalNameLocal: profile.legalNameLocal ?? '',
    companyRegNo: profile.companyRegNo ?? '',
  };
}

export function BranchTaxCard({ settings, settingsReadOnly }: { settings: Record<string, unknown>; settingsReadOnly: boolean }) {
  const { user, can, canWrite } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const branches = user?.branches ?? [];
  const [branchId, setBranchId] = useState<string>(branches[0]?.id ?? '');
  const readOnly = !can('branch.manage') || !canWrite;

  const view = useQuery({
    queryKey: ['branch-tax-profile', branchId],
    queryFn: () => get<BranchTaxProfileView>(`/org/branches/${branchId}/tax-profile`),
    enabled: Boolean(branchId),
  });
  const [form, setForm] = useState<TaxForm>(EMPTY);
  useEffect(() => setForm(formOf(view.data)), [view.data]);

  const save = useMutation({
    mutationFn: () =>
      put<{ warnings: { path: string; message: string }[] }>(`/org/branches/${branchId}/tax-profile`, {
        registrationNo: form.registrationNo.trim() || null,
        legalName: form.legalName.trim() || null,
        legalNameLocal: form.legalNameLocal.trim() || null,
        companyRegNo: form.companyRegNo.trim() || null,
      }),
    onSuccess: (result) => {
      toast.success(tr('admin', 'tax_registration_saved'));
      for (const warning of result.warnings ?? []) toast.error(tr('admin', 'please_check'), warning.message);
      void queryClient.invalidateQueries({ queryKey: ['branch-tax-profile', branchId] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_tax_registration'), describeError(error)),
  });

  const switches = useMutation({
    mutationFn: (patch: Record<string, boolean>) => put('/org/settings', patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] }),
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_setting'), describeError(error)),
  });

  if (!branches.length) return <Alert>{tr('admin', 'this_account_has_no_branch_so_there_is_no_tax')}</Alert>;

  const data = view.data;
  const card = data ? REGIME_CARDS[data.regime] : null;
  const locked = Boolean(data?.profile);
  const secondaryName = data?.documentSecondary === 'ar' ? 'Arabic' : data?.documentSecondary ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{card?.title ?? tr('admin', 'tax')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {branches.length > 1 && (
          <Field label={tr('admin', 'branch')}>
            <Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} — {branch.name}
                  {branch.country ? ` (${branch.country})` : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {view.isLoading && (
          <SkeletonText lines={3} />
        )}
        {view.isError && <Alert variant="destructive">{describeError(view.error)}</Alert>}
        {data && !data.country && <Alert>{tr('admin', 'set_this_branchs_country_in_branches_before')}</Alert>}
        {data && card && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {data.country && <Badge variant="outline">{data.country}</Badge>}
              {data.currency && <Badge variant="outline">{data.currency}</Badge>}
              {locked && <Badge variant="secondary">{tr('admin', 'country_and_currency_locked_by_this_registration')}</Badge>}
            </div>
            <Alert>{card.note}</Alert>
            {data.registrationLabel && (
              <Field label={data.registrationLabel} hint={card.taxNoHint}>
                <Input dir="ltr" value={form.registrationNo} disabled={readOnly} onChange={(e) => setForm({ ...form, registrationNo: e.target.value })} />
              </Field>
            )}
            {data.regime !== 'none' && (
              <Field label={tr('admin', 'legal_name_english')} hint={tr('admin', 'as_on_the_registration_defaults_to_the_display')}>
                <Input value={form.legalName} disabled={readOnly} onChange={(e) => setForm({ ...form, legalName: e.target.value })} />
              </Field>
            )}
            {card.localName && (
              <Field label={`Legal name (${secondaryName ?? 'local'})`} hint={card.localNameHint}>
                <Input dir="rtl" value={form.legalNameLocal} disabled={readOnly} onChange={(e) => setForm({ ...form, legalNameLocal: e.target.value })} />
              </Field>
            )}
            {data.companyRegistrationLabel && (
              <Field label={data.companyRegistrationLabel}>
                <Input dir="ltr" value={form.companyRegNo} disabled={readOnly} onChange={(e) => setForm({ ...form, companyRegNo: e.target.value })} />
              </Field>
            )}
            {!locked && data.regime !== 'none' && !readOnly && (
              <p className="text-xs text-muted-foreground">{tr('admin', 'saving_a_registration_locks_this_branchs')}</p>
            )}
            {!readOnly && data.regime !== 'none' && data.country && (
              <Button onClick={() => save.mutate()} loading={save.isPending}>
                {tr('admin', 'save_tax_registration')}
              </Button>
            )}
            {secondaryName && data.secondaryOnTaxDocuments === 'default_on' && (
              <Checkbox
                checked={settings.taxDocumentsSecondaryLanguage !== false}
                disabled={settingsReadOnly || switches.isPending}
                onCheckedChange={(checked) => switches.mutate({ taxDocumentsSecondaryLanguage: checked })}
                label={<strong>{tr('admin', 'print_x_beside_english_on_tax_invoices_and', { secondaryName: secondaryName })}</strong>}
                description={tr('admin', 'applies_to_every_branch_in_this_country')}
              />
            )}
            {secondaryName && data.secondaryOnTaxDocuments === 'mandatory' && (
              <p className="text-xs text-muted-foreground">{tr('admin', 'tax_invoices_and_credit_notes_always_print_in', { secondaryName: secondaryName })}</p>
            )}
            {secondaryName && (
              <Checkbox
                checked={settings.clinicalDocumentsSecondaryLanguage === true}
                disabled={settingsReadOnly || switches.isPending}
                onCheckedChange={(checked) => switches.mutate({ clinicalDocumentsSecondaryLanguage: checked })}
                label={<strong>{`Print ${secondaryName} beside English on the patient's copy of clinical documents.`}</strong>}
                description={tr('admin', 'prescriptions_dispensing_labels_and_discharge')}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
